/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  UserProfilePayloadResponse,
  UserStats,
  UpdateUserProfileRequest,
  UpdateUserProfileResponse,
  UserActivityRequest,
  UserActivity,
  UserActivityResponse,
  PersonalizationPreferencesResponse,
  UserActivityTypes,
} from 'src/types/UserProfile';
import FetchStatus from 'src/types/FetchStatus';
import {
  getUserProfileApi,
  getUserProfileActivitiesApi,
  PERSONALIZATION_PREFERENCES_API_ENDPOINT,
} from 'src/constants/apiEndpoints';
import {
  customFetch, constructQueryParams, parseErrorResponse, getFieldErrorList,
} from 'src/utils';
import { ValidationError } from 'src/types/Errors';
import { EditUserProfileErrors } from 'src/constants/errorStrings';
import { MerchantAccount } from 'src/types/MerchantAccounts';

export interface UserProfileState {
  username: string,
  userId?: string,
  isInternal: boolean,
  avatarSrc: string;
  aboutMe?: string;
  aboutMyBusiness?: string;
  businessNames?: string[];
  stats: UserStats;
  isDeleted?: boolean;
  oldIdentity?: string;
  createdAt?: Date;
  userActivities: UserActivity[];
  activityCursor: string | null;
  status: FetchStatus,
  updateStatus: FetchStatus,
  userActivityStatus: FetchStatus,
  error?: string,
  formValidationErrors?: ValidationError[]
  claimUsernameStatus: FetchStatus,
  personalizationPreferencesFetchStatus: FetchStatus,
  personalizationPreferencesUpdateStatus: FetchStatus,
  followedCategories: Array<string>,
  isDiscourseProfile?: boolean,
  alternateUserId?: string,
  merchantAccounts?: Array<MerchantAccount>,
  userActivityType: UserActivityTypes
}

export const initialState: UserProfileState = {
  username: '',
  isInternal: false,
  aboutMe: '',
  avatarSrc: '',
  aboutMyBusiness: '',
  stats: {
    upVotes: 0,
    downVotes: 0,
    discussionsStarted: 0,
    repliesToDiscussion: 0,
    solutionProvided: 0,
  },
  userActivityType: UserActivityTypes.all,
  userActivities: [],
  activityCursor: null,
  status: FetchStatus.idle,
  updateStatus: FetchStatus.idle,
  userActivityStatus: FetchStatus.idle,
  claimUsernameStatus: FetchStatus.idle,
  personalizationPreferencesFetchStatus: FetchStatus.idle,
  personalizationPreferencesUpdateStatus: FetchStatus.idle,
  merchantAccounts: [],
  followedCategories: [],
};

export const fetchPersonalizationPreferences = createAsyncThunk(
  'personalization/fetchfollowedCategories',
  async (_request, { rejectWithValue }) => customFetch(
    PERSONALIZATION_PREFERENCES_API_ENDPOINT,
    undefined,
    (error: Error) => rejectWithValue(error.message),
  ) as Promise<PersonalizationPreferencesResponse>,
);

export const updatePersonalizationPreferences = createAsyncThunk(
  'personalization/updatefollowedCategories',
  async (request: { followedCategories: Array<string> }, { rejectWithValue }) => customFetch(
    PERSONALIZATION_PREFERENCES_API_ENDPOINT,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
    (error: Error) => rejectWithValue(error.message),
  ),
);

export const fetchUserProfile = createAsyncThunk(
  'app/fetchUserProfile',
  async (userId: string, { rejectWithValue }) => customFetch(
    getUserProfileApi(userId),
    undefined,
    (error: Error) => rejectWithValue(error.message),
  ) as Promise<UserProfilePayloadResponse>,
);

export const updateUserProfile = createAsyncThunk(
  'app/updateUserProfile',
  async (
    {
      userId,
      username,
      avatarSrc,
      aboutMe,
      aboutMyBusiness,
    }: UpdateUserProfileRequest,
    { rejectWithValue },
  ) => await customFetch(
    getUserProfileApi(userId),
    {
      method: 'PUT',
      body: JSON.stringify({
        username,
        avatarSrc,
        aboutMe,
        aboutMyBusiness,
      }),
    },
    (error, responseInfo) => rejectWithValue(responseInfo!.data),
  ) as UpdateUserProfileResponse,
);

export const fetchUserActivity = createAsyncThunk(
  'app/fetchUserActivity',
  async ({
    cursor = null,
    userId,
    activityType,
  }: UserActivityRequest, { rejectWithValue }) => {
    const apiEndpoint = constructQueryParams({
      cursor: cursor! && encodeURIComponent(cursor),
      activityType,
    }, getUserProfileActivitiesApi(userId));

    return customFetch(
      apiEndpoint,
      undefined,
      (error: Error) => rejectWithValue(error.message),
    ) as Promise<UserActivityResponse>;
  },
);

export const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    reset: () => initialState,
    resetUpdateStatus: (state: UserProfileState) => {
      state.updateStatus = FetchStatus.idle;
    },
    resetErrors: (state: UserProfileState) => {
      state.error = undefined;
      state.formValidationErrors = undefined;
      state.updateStatus = FetchStatus.idle;
    },
    resetClaimUsernameStatus: (state: UserProfileState) => {
      state.claimUsernameStatus = FetchStatus.idle;
    },
    setUsername: (
      state: UserProfileState,
      action: PayloadAction<string>,
    ) => {
      state.username = action.payload;
    },
    setOldIdentity: (
      state: UserProfileState,
      action: PayloadAction<string | undefined>,
    ) => {
      state.oldIdentity = action.payload;
    },
    setUserActivityType: (
      state: UserProfileState,
      action: PayloadAction<UserActivityTypes>,
    ) => {
      state.userActivityType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.rejected, (state: UserProfileState, action) => {
        state.error = action.payload as string;
        state.status = FetchStatus.failed;
      })
      .addCase(fetchUserProfile.pending, (state: UserProfileState) => {
        state.status = FetchStatus.loading;
      })
      .addCase(fetchUserProfile.fulfilled, (state: UserProfileState, action) => {
        const {
          username,
          userId,
          avatarSrc,
          aboutMe,
          aboutMyBusiness,
          businessNames,
          stats,
          oldIdentity,
          internalUser,
          createdAt,
          isDiscourseProfile,
          alternateProfile,
          deleted,
          merchantAccounts,
        } = action.payload;
        Object.assign(state, {
          status: FetchStatus.complete,
          username,
          userId,
          avatarSrc,
          aboutMe,
          aboutMyBusiness,
          businessNames,
          stats,
          oldIdentity,
          createdAt,
          isInternal: internalUser,
          isDiscourseProfile,
          alternateUserId: alternateProfile?.userId,
          isDeleted: deleted,
          merchantAccounts,
        });
      })
      .addCase(updateUserProfile.rejected, (state: UserProfileState, action) => {
        /**
         * @todo check for 400 errors, add action validation errors once enums are
         * are finalized
         */
        const error = action.payload as any;
        if (error instanceof Object) {
          const parsedErrorResponse = parseErrorResponse(error);
          if (parsedErrorResponse && parsedErrorResponse.fieldErrors) {
            state.formValidationErrors = getFieldErrorList(
              parsedErrorResponse.fieldErrors,
              EditUserProfileErrors,
            );
          }
        }
        state.updateStatus = FetchStatus.failed;
      })
      .addCase(updateUserProfile.pending, (state: UserProfileState) => {
        state.formValidationErrors = undefined;
        state.error = undefined;
        state.updateStatus = FetchStatus.loading;
      })
      .addCase(updateUserProfile.fulfilled, (state: UserProfileState, action) => {
        const {
          username,
          avatarSrc,
          aboutMe,
          aboutMyBusiness,
        } = action.meta.arg;
        state.updateStatus = FetchStatus.complete;
        if (username) state.username = username;
        if (avatarSrc) state.avatarSrc = avatarSrc;
        if (typeof aboutMe === 'string') state.aboutMe = aboutMe;
        if (typeof aboutMyBusiness === 'string') state.aboutMyBusiness = aboutMyBusiness;
      })
      .addCase(fetchUserActivity.rejected, (state: UserProfileState, action) => {
        state.error = action.payload as string;
        state.userActivityStatus = FetchStatus.failed;
      })
      .addCase(fetchUserActivity.pending, (state: UserProfileState, action) => {
        const { cursor } = action.meta.arg;
        if (!cursor) {
          state.userActivities = [];
        }
        state.userActivityStatus = FetchStatus.loading;
      })
      .addCase(fetchUserActivity.fulfilled, (state: UserProfileState, action) => {
        const { userActivities, cursor } = action.payload;

        state.userActivityStatus = FetchStatus.complete;
        state.userActivities = state.userActivities.concat(userActivities);
        state.activityCursor = cursor;
      })
      .addCase(fetchPersonalizationPreferences.rejected, (state: UserProfileState) => {
        state.personalizationPreferencesFetchStatus = FetchStatus.failed;
      })
      .addCase(fetchPersonalizationPreferences.pending, (state: UserProfileState) => {
        state.personalizationPreferencesFetchStatus = FetchStatus.loading;
      })
      .addCase(fetchPersonalizationPreferences.fulfilled, (state: UserProfileState, action) => {
        state.personalizationPreferencesFetchStatus = FetchStatus.complete;
        state.followedCategories = action.payload.followedCategories;
      })
      .addCase(updatePersonalizationPreferences.rejected, (state: UserProfileState) => {
        state.personalizationPreferencesUpdateStatus = FetchStatus.failed;
      })
      .addCase(updatePersonalizationPreferences.pending, (state: UserProfileState) => {
        state.personalizationPreferencesUpdateStatus = FetchStatus.loading;
      })
      .addCase(updatePersonalizationPreferences.fulfilled, (state: UserProfileState, action) => {
        state.personalizationPreferencesUpdateStatus = FetchStatus.complete;
        state.followedCategories = action.meta.arg.followedCategories;
      });
  },
});
export const userProfileActions = userProfileSlice.actions;
export default userProfileSlice.reducer;
